/* External */
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';

/* Components */
import FocusableButton from '../FocusableButton';
import QrCode from '../QrCode';

/* Utils */
import { useErrorMessages } from './utils';
import { useOnBackPress } from '../../utils/keyDownEvents';
import platform from '../../utils/platform';
import { getGtmPlatform } from '../../utils/googleTags/utils';
import { redirectTo } from '../../utils/history';
import { getLink, Routes } from '../../utils/router';
import { trackGAErrorMessageButton, trackGAErrorMessagePlayer } from '../../utils/trackGAData';
import { UPSELL_PAY_PER_VIEW_CAMPAIGN_NAME } from '../../utils/env';

/* Styles */
import './UpSellMessage.scss';

import {
  formatContentData,
  GTM_EVENT_NAMES,
  GTM_CATEGORIES,
  GTM_SUBCATEGORIES,
  GTM_ERROR_BLOCK_CONTENT,
  GTM_PLATFORMS_ERROR_CODES,
} from '../../utils/googleTags';

import { useGtmLocalContext } from '../../providers/GtmLocalProvider';

function UpSellMessage({
  content = undefined,
  error,
  contentTitle = '',
  channelId = '',
  exit = () => {},
  setFocus,
  sourceProvider = '',
  isFromInterstitial = false,
  isCurrentInterstitialSchedule = undefined,
}) {
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(false);
  const gtmPlatform = getGtmPlatform(platform?.aemPlatform);

  const errorType = GTM_ERROR_BLOCK_CONTENT.NOT_IN_PACKAGE;
  const campaignName = UPSELL_PAY_PER_VIEW_CAMPAIGN_NAME;

  const isInterstitialCurrentScheduleError = isFromInterstitial && isCurrentInterstitialSchedule;

  const {
    title,
    subtitle,
    linkURL,
    qrLink,
    qrCodeTitle,
    isAPayPerViewUpSell,
    isAPayPerViewUpSellGranHermano,
    highlightImageURL,
    backgroundImageURL,
    primaryButtonTitle,
    secondaryButtonTitle,
  } = useErrorMessages(error, contentTitle, channelId);

  const upSellMessageTagEvent = useCallback(({ eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PLAYER_MOVIE_SERIE_LIVE_DATA,
        subCategory: GTM_SUBCATEGORIES.PLAYER.PLAYER_ERROR,
        eventName,
        data: {
          content: {
            ...formatContentData({ content }),
          },
          errorType,
          errorCode: GTM_PLATFORMS_ERROR_CODES[gtmPlatform],
          campaignName,
        },
      });
    }
  }, [commonTaggingEvent, gtmPlatform, errorType, campaignName, content]);

  const closePlayer = useCallback(() => {
    exit();
    trackGAErrorMessageButton(error);
  }, [error, exit]);

  const goBackToHome = useCallback(() => {
    if (isAPayPerViewUpSellGranHermano) {
      upSellMessageTagEvent({
        eventName: GTM_EVENT_NAMES.PLAYER.YA_COMPRE_PPV,
      });
    } else {
      upSellMessageTagEvent({
        eventName: GTM_EVENT_NAMES.PLAYER.OK,
      });
    }

    trackGAErrorMessageButton(error);
    redirectTo(getLink(Routes.Private.Home));
  }, [error, upSellMessageTagEvent, isAPayPerViewUpSellGranHermano]);

  const goBackToPreviousPage = useCallback(() => {
    upSellMessageTagEvent({
      eventName: GTM_EVENT_NAMES.PLAYER.OK,
    });
    closePlayer();
  }, [closePlayer, upSellMessageTagEvent]);

  const onPrimaryButtonPress = isAPayPerViewUpSellGranHermano ? goBackToHome : goBackToPreviousPage;

  const onSecondaryButtonPress = goBackToPreviousPage;

  useOnBackPress({
    listener: useCallback(() => {
      upSellMessageTagEvent({
        eventName: GTM_EVENT_NAMES.PLAYER.CLOSE,
      });
      closePlayer();
    }, [closePlayer, upSellMessageTagEvent]),
  });

  const upSellMessageClasses = useMemo(() => (
    `dtv-html5-c-up-sell-message
    ${isAPayPerViewUpSellGranHermano ? 'dtv-html5-c-up-sell-message--pay-per-view-background' : ''}`
  ), [isAPayPerViewUpSellGranHermano]);

  const containerClasses = useMemo(() => (
    `dtv-html5-c-up-sell-message__container
    ${linkURL ? 'dtv-html5-c-up-sell-message__container--with-link' : ''}`
  ), [linkURL]);

  const logoClasses = useMemo(() => (
    `dtv-html5-c-up-sell-message__logo ${isAPayPerViewUpSellGranHermano ? 'dtv-html5-c-up-sell-message__pay-per-view-logo' : ''}`
  ), [isAPayPerViewUpSellGranHermano]);

  const qrCodeClasses = useMemo(() => (
    `${isAPayPerViewUpSellGranHermano
      ? 'dtv-html5-c-up-sell-message__pay-per-view-qr-code'
      : 'dtv-html5-c-up-sell-message__qr-code'
    }`
  ), [isAPayPerViewUpSellGranHermano]);

  useEffect(() => {
    setFocus();
    if (!tagsMountRef.current && content) {
      upSellMessageTagEvent({
        eventName: isAPayPerViewUpSell ? GTM_EVENT_NAMES.PAGE_VIEW_PPV : GTM_EVENT_NAMES.PAGE_VIEW,
        content,
      });
      tagsMountRef.current = true;
    }
  }, [setFocus, upSellMessageTagEvent, isAPayPerViewUpSell, content]);

  useEffect(() => {
    trackGAErrorMessagePlayer({
      error,
      title,
      subtitle,
      sourceProvider,
      isFromInterstitial,
      isInterstitialCurrentScheduleError,
    });
  }, [
    sourceProvider,
    subtitle,
    title,
    isFromInterstitial,
    isInterstitialCurrentScheduleError,
    error,
  ]);

  return (
    <div style={{ backgroundImage: `url(${backgroundImageURL})` }} className={upSellMessageClasses}>
      <div className={containerClasses}>
        <div className={`dtv-html5-c-up-sell-message__content ${linkURL ? '' : 'dtv-html5-c-up-sell-message__content--center'}`}>
          <img className={logoClasses} src={highlightImageURL} alt="dtv-logo" />
          <span className="dtv-html5-c-up-sell-message__title">
            {title}
          </span>
          <span className="dtv-html5-c-up-sell-message__subtitle">
            {subtitle}
          </span>
          {linkURL && (
            <span className="dtv-html5-c-up-sell-message__link">
              {linkURL}
            </span>
          )}
          <div className="dtv-html5-c-up-sell-message__buttons-container">
            <FocusableButton
              className="dtv-html5-c-up-sell-message__button"
              onEnterPress={onPrimaryButtonPress}
              primary
              bordered
            >
              {primaryButtonTitle || <FormattedMessage id="common.back" />}
            </FocusableButton>
            {isAPayPerViewUpSellGranHermano && secondaryButtonTitle && (
              <FocusableButton
                className="dtv-html5-c-up-sell-message__button"
                onEnterPress={onSecondaryButtonPress}
                primary
                bordered
              >
                {secondaryButtonTitle}
              </FocusableButton>
            )}
          </div>
        </div>

        {(qrLink || linkURL) && (
          <div className="dtv-html5-c-up-sell-message__qr-code-content">
            <QrCode
              data={qrLink || linkURL}
              className={qrCodeClasses}
            />
            {isAPayPerViewUpSellGranHermano && qrCodeTitle && (
              <span className="dtv-html5-c-up-sell-message__qr-code-title">
                {qrCodeTitle}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

UpSellMessage.propTypes = {
  content: PropTypes.shape({}),
  error: PropTypes.shape({
    data: PropTypes.shape({
      response: PropTypes.shape({
        data: PropTypes.shape({
          details: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
  contentTitle: PropTypes.string,
  channelId: PropTypes.string,
  exit: PropTypes.func,
  setFocus: PropTypes.func.isRequired,
  sourceProvider: PropTypes.string,
  isFromInterstitial: PropTypes.bool,
  isCurrentInterstitialSchedule: PropTypes.bool,
};

export default memo(withFocusable()(memo(UpSellMessage)));
